import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Copy, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  owner: "#00E5FF", admin: "#84CC16", operator: "#EAB308",
  auditor: "#F97316", viewer: "#94A3B8",
};

export default function Members() {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [me, setMe] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ role: "operator" });
  const [invited, setInvited] = useState(null);

  const load = () => {
    api.get("/members").then((r) => setMembers(r.data.members));
    api.get("/roles").then((r) => { setRoles(r.data.roles); setMe(r.data.current); });
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email || !form.full_name) return toast.error("Nombre y email");
    try {
      const { data } = await api.post("/members/invite", form);
      setInvited(data);
      toast.success(`Invitado ${data.email}`);
      setForm({ role: "operator" });
      setDlg(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/members/${m.id}/role`, { role });
      toast.success(`${m.email} → ${role}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    }
  };

  const copyPw = (pw) => {
    navigator.clipboard.writeText(pw);
    toast.success("Contraseña copiada");
  };

  return (
    <div className="p-8 space-y-6" data-testid="members-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">RBAC · MULTI-USUARIO</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Miembros</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Invita a tu equipo con roles granulares. Solo el owner puede cambiar roles.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="mem-invite">
              <UserPlus className="w-4 h-4 mr-2" /> Invitar miembro
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader><DialogTitle className="font-display">Invitar miembro</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Nombre</Label>
                <Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="mem-name" />
              </div>
              <div>
                <Label className="label-mono">Email</Label>
                <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       type="email" className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="mem-email" />
              </div>
              <div>
                <Label className="label-mono">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="mem-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                    {roles.filter((r) => r.id !== "owner").map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label} — {r.desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={invite} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="mem-submit">
                Invitar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {invited && (
        <div className="border border-[var(--warn)] bg-[var(--warn)]/5 p-4" data-testid="mem-invited">
          <div className="label-mono mb-2" style={{ color: "var(--warn)" }}>CONTRASEÑA TEMPORAL — CÓPIALA AHORA</div>
          <div className="flex items-center gap-3">
            <code className="font-mono text-sm text-white bg-[var(--bg)] px-3 py-2 border border-[var(--border)] flex-1">
              {invited.email} · {invited.temporary_password}
            </code>
            <Button onClick={() => copyPw(invited.temporary_password)} variant="outline" className="rounded-none" data-testid="mem-copy">
              <Copy className="w-3 h-3 mr-2" /> Copiar
            </Button>
            <button onClick={() => setInvited(null)} className="label-mono">✕</button>
          </div>
        </div>
      )}

      <div className="border border-[var(--border)] bg-[var(--surface)]">
        <div className="grid grid-cols-[1fr_2fr_180px_150px] label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]">
          <div>NOMBRE</div>
          <div>EMAIL</div>
          <div>ROL</div>
          <div className="text-right">CREADO</div>
        </div>
        {members.map((m) => (
          <div key={m.id} className="grid grid-cols-[1fr_2fr_180px_150px] py-3 px-4 border-b border-[var(--border)] items-center" data-testid={`mem-${m.id}`}>
            <div className="text-sm text-white font-medium">{m.full_name}</div>
            <div className="font-mono text-xs text-[var(--text-dim)]">{m.email}</div>
            <div>
              {me === "owner" && m.role !== "owner" ? (
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                  <SelectTrigger className="w-40 rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs h-8" data-testid={`mem-role-${m.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                    {roles.filter((r) => r.id !== "owner").map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge className="rounded-none font-mono text-[10px]" style={{ background: ROLE_COLORS[m.role], color: "#0B0E14" }}>
                  {m.role.toUpperCase()}
                </Badge>
              )}
            </div>
            <div className="font-mono text-[10px] text-[var(--text-dim)] text-right">
              {new Date(m.created_at).toLocaleDateString("es-ES")}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {roles.map((r) => (
          <div key={r.id} className="border border-[var(--border)] bg-[var(--surface)] p-4">
            <div className="label-mono mb-1" style={{ color: ROLE_COLORS[r.id] }}>{r.label.toUpperCase()}</div>
            <div className="text-xs text-[var(--text-dim)] leading-relaxed">{r.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
